import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../lib/api/config';
import toast from 'react-hot-toast';

interface WebSocketEvents {
  'event:changed': (data: {
    action: 'created' | 'updated' | 'deleted';
    event: any;
    timestamp: string;
  }) => void;
  'availability:changed': (data: {
    userId: string;
    availability: any;
    timestamp: string;
  }) => void;
  'user:settings:changed': (data: {
    userId: string;
    settings: any;
    timestamp: string;
  }) => void;
  'user:connected': (data: {
    userId: string;
    email: string;
  }) => void;
  'user:disconnected': (data: {
    userId: string;
    email: string;
  }) => void;
  'notification': (data: any) => void;
}

export function useWebSocket() {
  const { token } = useAuth();
  const socketRef = useRef<Socket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const isConnectedRef = useRef(false);
  const isPageVisibleRef = useRef(!document.hidden);
  const errorToastTimeoutRef = useRef<NodeJS.Timeout>();

  const connect = useCallback(() => {
    if (!token || socketRef.current?.connected) return;

    const wsUrl = API_URL.replace('/api', '').replace('http', 'ws');
    
    socketRef.current = io(wsUrl, {
      transports: ['websocket', 'polling'],
      timeout: 20000,
      forceNew: true
    });

    const socket = socketRef.current;

    socket.on('connect', () => {
      isConnectedRef.current = true;
      
      // Authenticate with the server
      socket.emit('authenticate', token);
      
      // Clear any pending reconnection
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = undefined;
      }
      
      // Clear any pending error toast (successful reconnection)
      if (errorToastTimeoutRef.current) {
        clearTimeout(errorToastTimeoutRef.current);
        errorToastTimeoutRef.current = undefined;
      }
    });

    socket.on('authenticated', (data) => {
      // Authentication successful
    });

    socket.on('auth:error', (error) => {
      console.error('❌ WebSocket authentication failed:', error);
      socket.disconnect();
    });

    socket.on('disconnect', (reason) => {
      isConnectedRef.current = false;
      
      // Clear any pending error toast
      if (errorToastTimeoutRef.current) {
        clearTimeout(errorToastTimeoutRef.current);
        errorToastTimeoutRef.current = undefined;
      }
      
      // Only show toast for unexpected disconnections while page is visible
      if (reason !== 'io client disconnect') {
        // Add grace period before showing error toast
        // Skip toast if page is hidden (browser throttling WebSocket connections)
        errorToastTimeoutRef.current = setTimeout(() => {
          // Double-check connection status and page visibility after grace period
          if (!socketRef.current?.connected && isPageVisibleRef.current) {
            toast.error('Lost connection to real-time updates', { duration: 3000 });
          }
          errorToastTimeoutRef.current = undefined;
        }, 2000); // 2-second grace period
        
        // Attempt to reconnect after a delay
        reconnectTimeoutRef.current = setTimeout(() => {
          if (token && !socketRef.current?.connected) {
            connect();
          }
        }, 3000);
      }
    });

    socket.on('connect_error', (error) => {
      console.error('🔌 WebSocket connection error:', error);
      isConnectedRef.current = false;
    });

    return socket;
  }, [token]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }
    
    if (errorToastTimeoutRef.current) {
      clearTimeout(errorToastTimeoutRef.current);
      errorToastTimeoutRef.current = undefined;
    }
    
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      isConnectedRef.current = false;
    }
  }, []);

  const on = useCallback(<K extends keyof WebSocketEvents>(
    event: K,
    handler: WebSocketEvents[K]
  ) => {
    if (socketRef.current) {
      socketRef.current.on(event, handler);
    }
  }, []);

  const off = useCallback(<K extends keyof WebSocketEvents>(
    event: K,
    handler?: WebSocketEvents[K]
  ) => {
    if (socketRef.current) {
      if (handler) {
        socketRef.current.off(event, handler);
      } else {
        socketRef.current.off(event);
      }
    }
  }, []);

  const emit = useCallback((event: string, data?: any) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, data);
    }
  }, []);

  const joinCalendarDate = useCallback((date: string) => {
    emit('calendar:join', { date });
  }, [emit]);

  const leaveCalendarDate = useCallback((date: string) => {
    emit('calendar:leave', { date });
  }, [emit]);

  const isConnected = useCallback(() => {
    return isConnectedRef.current && socketRef.current?.connected;
  }, []);

  // Connect when token is available
  useEffect(() => {
    if (token) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [token, connect, disconnect]);

  // Track page visibility to avoid showing disconnection errors when tab is hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      isPageVisibleRef.current = !document.hidden;
      
      // If page becomes visible and we're disconnected, attempt immediate reconnection
      if (!document.hidden && token && !socketRef.current?.connected) {
        connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, connect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    connect,
    disconnect,
    on,
    off,
    emit,
    joinCalendarDate,
    leaveCalendarDate,
    isConnected
  };
}